import { EXTERNAL_LOAN_DEFAULT_DAILY_INTEREST_RATE_PERCENT } from '../constants/externalLoanConstants';

const toFiniteNumber = (value) => {
  if (value === '' || value === null || value === undefined) return null;

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

export const calculateExternalLoanTerms = ({
  loanAmount,
  dailyInterestRatePercent = EXTERNAL_LOAN_DEFAULT_DAILY_INTEREST_RATE_PERCENT,
  numberOfDays,
  maximumEligibleAmount,
} = {}) => {
  const parsedLoanAmount = toFiniteNumber(loanAmount);
  const hasLoanAmount = parsedLoanAmount !== null && parsedLoanAmount > 0;
  const parsedDailyInterestRatePercent = toFiniteNumber(dailyInterestRatePercent);
  const hasDailyInterestRate = parsedDailyInterestRatePercent !== null && parsedDailyInterestRatePercent > 0;
  const parsedNumberOfDays = toFiniteNumber(numberOfDays);
  const hasNumberOfDays = parsedNumberOfDays !== null
    && Number.isInteger(parsedNumberOfDays)
    && parsedNumberOfDays >= 1;
  const safeLoanAmount = hasLoanAmount ? parsedLoanAmount : 0;
  const safeDailyInterestRatePercent = hasDailyInterestRate ? parsedDailyInterestRatePercent : 0;
  const safeNumberOfDays = hasNumberOfDays ? parsedNumberOfDays : 0;
  const calculatedDailyInterestAmount = safeLoanAmount * (safeDailyInterestRatePercent / 100);
  const dailyInterestAmount = Number.isFinite(calculatedDailyInterestAmount) ? calculatedDailyInterestAmount : 0;
  const calculatedTotalInterest = dailyInterestAmount * safeNumberOfDays;
  const totalInterest = Number.isFinite(calculatedTotalInterest) ? calculatedTotalInterest : 0;
  const calculatedTotalPayable = safeLoanAmount + totalInterest;
  const totalPayable = Number.isFinite(calculatedTotalPayable) ? calculatedTotalPayable : 0;
  const parsedMaximumEligibleAmount = toFiniteNumber(maximumEligibleAmount);
  const hasMaximumEligibleAmount = parsedMaximumEligibleAmount !== null && parsedMaximumEligibleAmount >= 0;
  const canCalculateMaximumDays = hasLoanAmount
    && hasDailyInterestRate
    && hasMaximumEligibleAmount
    && safeLoanAmount < parsedMaximumEligibleAmount
    && dailyInterestAmount > 0;
  const calculatedMaximumAllowedDays = canCalculateMaximumDays
    ? Math.floor((parsedMaximumEligibleAmount - safeLoanAmount) / dailyInterestAmount)
    : 0;
  const maximumAllowedDays = Number.isFinite(calculatedMaximumAllowedDays)
    ? Math.max(0, calculatedMaximumAllowedDays)
    : 0;

  return {
    principal: safeLoanAmount,
    dailyInterestRatePercent: safeDailyInterestRatePercent,
    dailyInterestAmount,
    totalInterest,
    totalPayable,
    numberOfDays: safeNumberOfDays,
    maximumAllowedDays,
    valid: hasLoanAmount && hasDailyInterestRate && hasNumberOfDays,
  };
};

export const calculateExternalLoanEligibilityStatus = ({
  totalPayable,
  maximumEligibleAmount,
} = {}) => {
  const parsedTotalPayable = toFiniteNumber(totalPayable);
  const parsedMaximumEligibleAmount = toFiniteNumber(maximumEligibleAmount);
  const hasTotalPayable = parsedTotalPayable !== null && parsedTotalPayable >= 0;
  const hasMaximumEligibleAmount = parsedMaximumEligibleAmount !== null && parsedMaximumEligibleAmount >= 0;
  const safeTotalPayable = hasTotalPayable ? parsedTotalPayable : 0;
  const safeMaximumEligibleAmount = hasMaximumEligibleAmount ? parsedMaximumEligibleAmount : 0;
  const remainingEligibility = safeMaximumEligibleAmount - safeTotalPayable;
  const exceededAmount = Math.max(0, -remainingEligibility);
  const isWithinEligibility = hasTotalPayable
    && hasMaximumEligibleAmount
    && remainingEligibility >= 0;

  return {
    maximumEligibleAmount: safeMaximumEligibleAmount,
    totalPayable: safeTotalPayable,
    remainingEligibility,
    exceededAmount,
    isWithinEligibility,
    status: isWithinEligibility ? 'VALID' : 'EXCEEDED',
  };
};
